import { useMemo } from "react";

/**
 * 지역 필터 chip strip — 상단 가로 스크롤.
 *
 * 정렬 규칙 (시별 그룹 순서 고정):
 *  1. "전체"
 *  2. "제주시" + 제주시 소속 읍/면 (애월·한림·조천·구좌)
 *  3. ─ 그룹 구분선 ─
 *  4. "서귀포시" + 서귀포시 소속 읍/면 (대정·안덕·남원·성산·표선)
 *
 * 카페가 0개인 지역(또는 `enabled: false`)은 회색 disabled 처리.
 * 매칭 로직(`match`)과 polygon 매처(`lib/regions.js`)는 미리 다 등록되어 있어서,
 * 나중에 카페가 추가되면 자동으로 활성화됨.
 */

const BASE_REGIONS = [
  { k: "all", lbl: "전체", match: () => true, enabled: true },

  // ── 제주시 그룹 ──────────────────────────────────
  { k: "jeju", lbl: "제주시", match: (n) => n === "제주시", enabled: true, group: "jeju" },
  { k: "aewol", lbl: "애월", match: (n) => n.includes("애월읍"), enabled: true, group: "jeju" },
  { k: "hallim", lbl: "한림", match: (n) => n.includes("한림읍") || n.includes("한경면"), enabled: true, group: "jeju" },
  { k: "jocheon", lbl: "조천", match: (n) => n.includes("조천읍"), enabled: true, group: "jeju" },
  { k: "gujwa", lbl: "구좌", match: (n) => n.includes("구좌읍"), enabled: true, group: "jeju" },

  // ── 서귀포시 그룹 ────────────────────────────────
  { k: "seogwipo", lbl: "서귀포시", match: (n) => n === "서귀포시", enabled: true, group: "seogwipo" },
  { k: "daejeong", lbl: "대정", match: (n) => n.includes("대정읍"), enabled: true, group: "seogwipo" },
  { k: "andeok", lbl: "안덕", match: (n) => n.includes("안덕면"), enabled: true, group: "seogwipo" },
  { k: "namwon", lbl: "남원", match: (n) => n.includes("남원읍"), enabled: true, group: "seogwipo" },
  { k: "seongsan", lbl: "성산", match: (n) => n.includes("성산읍"), enabled: true, group: "seogwipo" },
  { k: "pyoseon", lbl: "표선", match: (n) => n.includes("표선면"), enabled: true, group: "seogwipo" },
];

// 외부에서 쓰던 export 유지 (App.jsx 등에서 filterByRegion 사용)
export const REGIONS = BASE_REGIONS;

function countCafes(region, cafes) {
  if (region.k === "all") return cafes.length;
  return cafes.filter((c) => region.match(c.neighborhood || "")).length;
}

export default function RegionChips({ active, onChange, cafes = [] }) {
  const regionsWithCount = useMemo(
    () => BASE_REGIONS.map((r) => ({ ...r, count: countCafes(r, cafes) })),
    [cafes]
  );

  return (
    <div className="cf-chips-row">
      {regionsWithCount.map((r, i) => {
        const isEmpty = r.k !== "all" && r.count === 0;
        const isDisabled = r.enabled === false || isEmpty;
        const title = isEmpty
          ? "이 지역엔 아직 등록된 카페가 없어요"
          : r.enabled === false
          ? "준비 중"
          : undefined;

        // 서귀포시 그룹 시작 직전에 시각적 구분선 — 제주시 그룹과 분리
        const prev = regionsWithCount[i - 1];
        const showDivider = prev && prev.group === "jeju" && r.group === "seogwipo";

        return (
          <span key={r.k} style={{ display: "contents" }}>
            {showDivider && <span className="cf-chips-divider" aria-hidden="true" />}
            <button
              type="button"
              className={`cf-region-chip ${active === r.k ? "is-active" : ""} ${isDisabled ? "is-disabled" : ""}`}
              onClick={() => {
                if (isDisabled) return;
                onChange(r.k);
              }}
              disabled={isDisabled}
              aria-disabled={isDisabled}
              title={title}
            >
              {r.lbl}
            </button>
          </span>
        );
      })}
    </div>
  );
}

/** 주어진 region 키로 카페 리스트 필터링 */
export function filterByRegion(cafes, regionKey) {
  const region = BASE_REGIONS.find((r) => r.k === regionKey);
  if (!region || regionKey === "all") return cafes;
  return cafes.filter((c) => region.match(c.neighborhood || ""));
}
